import React, { ReactNode } from 'react';
import { StackVideoPlayerContextProvider } from '@jbhi-fi/components/src/hooks/useStackVideoPlayer';
import { ProductClient } from '../../../../../api/algolia/ProductClient';
import { ErrorBoundary } from '@jbhi-fi/components/src/ErrorBoundary/ErrorBoundary';
import { ProductClientProvider } from '@jbhi-fi/components/src/context/ProductClientProvider';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ShopifyCustomerProvider } from '../../../../../contexts/ShopifyCustomerProvider';
import { PublicApiClientProvider } from '@jbhi-fi/components/src/context/PublicApiClientProvider';
import { KlaviyoOnSiteClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoOnSiteClient';
import GlobalStyles from '@jbhi-fi/lanyard-ui/themes/GlobalStyles';
import { ShopifyProductCompareProvider } from '../../../../../contexts/ShopifyProductCompareContext';
import {
  ShopifyProductActionsProvider,
  ShopifyProductActionsProviderProps,
} from '../../../../../contexts/ShopifyProductActionsProvider';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { serviceHelper } from '../../../../../config/service-helper';
import { YouTubeIframeApiProvider } from '@jbhi-fi/components/src/context/YouTubeIframeApiProvider';
import { ShopifyProductProvider } from '../../../../../api/shopify/ShopifyProduct';
import { MinicartUiContextProvider } from '../../../../../utils/cart/context/MinicartUiContext';
import { CartContextProvider } from '../../../../../utils/cart/context/CartContext';
import { TaxInvoicesClient } from '@jbhi-fi/apis/src/taxInvoices/TaxInvoicesClient';
import { ShopifyEventsProvider } from '../../../../../contexts/Events';
import { LocalisationContextProvider } from '../../../localisation/context/LocalisationContext';
import { CustomerApiProvider } from '../../../customers/hooks/customerApi';
import { MobileVerifyProvider } from '../../../customers/context/MobileVerifyContext';
import { getFulfilmentClient } from '../../../../../api/fulfilment/getFulfilmentClient';
import { OrderTrackingThemeApiClient } from '@jbhi-fi/apis/src/orderTrackingThemeApi/OrderTrackingThemeApiClient';
import { StoreClient } from '@jbhi-fi/apis/src/algolia/StoreClient';
import { baseConfig, jbConfig } from '../../../../../config/algolia';
import { KlaviyoClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoClient';
import { KlaviyoBackInStoreClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoBackInStoreClient';
import { CouponContextProvider } from '../../../../../contexts/CouponContextProvider';
import { SubscriptionApiProvider } from '../../../customers/hooks/subscriptionApi';
import { SubscriptionTypes } from '../../../customers/api/subscriptions/types';
import { ConfigProvider, ConfigProviderProps } from '@jbhi-fi/components/src/context/ConfigProvider';
import { CurrentBreakpointProvider } from '@jbhi-fi/components/src/context/CurrentBreakpointProvider';
import { ServicesApiClient } from '@jbhi-fi/apis/src/services/ServicesApiClient';
import { AlternativesContextProvider } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';

export type BaseContentProviderProps = {
  children: ReactNode;
  shopifyProductActionsProviderProps?: Omit<ShopifyProductActionsProviderProps, 'children'>;
  configProviderProps?: ConfigProviderProps;
};

const queryClient = new QueryClient();
const productClient = new ProductClient();
const klaviyoOnSiteClient = new KlaviyoOnSiteClient({
  klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
  klaviyoSubscribeUrl: process.env.PUBLIC_KLAVIYO_NOTIFY_ME_URL || '',
  klaviyoNotifyMeListId: window.Jbhifi.klaviyoMailingListId,
});
const klaviyoClient = new KlaviyoClient({
  klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
  klaviyoSubscribeUrl: process.env.PUBLIC_KLAVIYO_SUBSCRIBE_URL || '',
});
const klaviyoBackInStoreClient = new KlaviyoBackInStoreClient({
  klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
  klaviyoBackInStoreUrl: process.env.PUBLIC_KLAVIYO_BACK_IN_STOCK_SUBSCRIPTIONS_URL || '',
});
const contentfulThemeApiClient = new ContentfulThemeApiClient({ themeApiBaseUrl: serviceHelper.themeApisUrl });
const taxInvoicesClient = new TaxInvoicesClient({
  baseUrl: `${serviceHelper.themeApisUrl}/taxInvoice`,
  refreshTokenUrl: serviceHelper.customerAuthRefresh,
});
const fulfilmentClient = getFulfilmentClient();
const orderTrackingThemeApiClient = new OrderTrackingThemeApiClient({
  themeApiBaseUrl: serviceHelper.themeApisUrl ?? '',
});
const algoliaStoreApiClient = new StoreClient({
  algoliaSearchIndex: jbConfig.algliaStoreLocatorIndex || '',
  algoliaAppId: baseConfig.app_id || '',
  algoliaSearchApiKey: baseConfig.search_api_key || '',
});
const servicesApiClient = new ServicesApiClient({
  serviceSuggestionsUrl: serviceHelper.serviceSuggestionsUrl,
  countryCode: window.Jbhifi.storeLocation,
});

export const BaseContentProvider = ({
  children,
  shopifyProductActionsProviderProps,
  configProviderProps = {},
}: BaseContentProviderProps) => {
  return (
    <>
      <GlobalStyles />
      <ErrorBoundary>
        <ConfigProvider
          contentfulImageQuality={window.Jbhifi.contentfulSettings?.defaultContentfulImageQuality}
          {...configProviderProps}
        >
          <CurrentBreakpointProvider>
            <YouTubeIframeApiProvider>
              <StackVideoPlayerContextProvider>
                <QueryClientProvider client={queryClient}>
                  <PublicApiClientProvider
                    klaviyoOnSiteClient={klaviyoOnSiteClient}
                    contentfulThemeApiClient={contentfulThemeApiClient}
                    taxInvoicesClient={taxInvoicesClient}
                    fulfilmentClient={fulfilmentClient}
                    orderTrackingThemeApiClient={orderTrackingThemeApiClient}
                    algoliaStoreApiClient={algoliaStoreApiClient}
                    klaviyoClient={klaviyoClient}
                    klaviyoBackInStoreClient={klaviyoBackInStoreClient}
                    servicesApiClient={servicesApiClient}
                  >
                    <ProductClientProvider client={productClient}>
                      <CartContextProvider>
                        <ShopifyCustomerProvider>
                          <ShopifyEventsProvider>
                            <ShopifyProductCompareProvider>
                              <ShopifyProductProvider>
                                <MinicartUiContextProvider>
                                  <LocalisationContextProvider>
                                    <MobileVerifyProvider>
                                      <CustomerApiProvider
                                        linkedId={{
                                          sourceSystem: 'Shopify',
                                          sourceSystemId: window.Jbhifi?.shopifyCustomerId,
                                        }}
                                      >
                                        <SubscriptionApiProvider subscriptionTypes={[SubscriptionTypes.perks]}>
                                          <CouponContextProvider>
                                            <ShopifyProductActionsProvider
                                              attachPageType="stack"
                                              {...shopifyProductActionsProviderProps}
                                            >
                                              <AlternativesContextProvider
                                                alternativesGoogleRecAiModel={
                                                  window.smartModuleSettings?.alternativesGoogleRecModelType
                                                }
                                              >
                                                {children}
                                              </AlternativesContextProvider>
                                            </ShopifyProductActionsProvider>
                                          </CouponContextProvider>
                                        </SubscriptionApiProvider>
                                      </CustomerApiProvider>
                                    </MobileVerifyProvider>
                                  </LocalisationContextProvider>
                                </MinicartUiContextProvider>
                              </ShopifyProductProvider>
                            </ShopifyProductCompareProvider>
                          </ShopifyEventsProvider>
                        </ShopifyCustomerProvider>
                      </CartContextProvider>
                    </ProductClientProvider>
                  </PublicApiClientProvider>
                </QueryClientProvider>
              </StackVideoPlayerContextProvider>
            </YouTubeIframeApiProvider>
          </CurrentBreakpointProvider>
        </ConfigProvider>
      </ErrorBoundary>
    </>
  );
};
